import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_my_products,
  create_product,
  update_product,
  delete_product,
  link_catalog_product,
  fetch_public_pharmacy_products,
  create_variant,
  update_variant,
  delete_variant,
} from './api';
import type { CreateProductInput, LinkCatalogProductInput, UpdateProductInput } from './types';
import type { CreateVariantInput, UpdateVariantInput } from '@/features/product-variant/types';

const MY_PRODUCTS = ['pharmacy-products'] as const;

export function use_my_products() {
  return useQuery({ queryKey: MY_PRODUCTS, queryFn: fetch_my_products });
}

/** Public catalog for the owner-side vendor detail page (available products only). */
export function use_public_pharmacy_products(vendor_id?: string) {
  return useQuery({
    queryKey: ['public-pharmacy-products', vendor_id],
    queryFn: () => fetch_public_pharmacy_products(vendor_id as string),
    enabled: !!vendor_id,
  });
}

function useInvalidatingMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: MY_PRODUCTS }) });
}

export const use_create_product = () => useInvalidatingMutation((i: CreateProductInput) => create_product(i));
export const use_link_catalog_product = () =>
  useInvalidatingMutation((i: LinkCatalogProductInput) => link_catalog_product(i));
export const use_update_product = () =>
  useInvalidatingMutation(({ id, input }: { id: string; input: UpdateProductInput }) => update_product(id, input));
export const use_delete_product = () => useInvalidatingMutation((id: string) => delete_product(id));

// ---- Variants ----
export const use_create_pharmacy_variant = () =>
  useInvalidatingMutation(({ product_id, input }: { product_id: string; input: CreateVariantInput }) =>
    create_variant(product_id, input),
  );
export const use_update_pharmacy_variant = () =>
  useInvalidatingMutation(({ id, input }: { id: string; input: UpdateVariantInput }) => update_variant(id, input));
export const use_delete_pharmacy_variant = () => useInvalidatingMutation((id: string) => delete_variant(id));
